import { useTranslation } from "@sagot/i18n";
import { useCallback, useEffect, useMemo } from "react";
import { useAgentList, useAudienceLabels } from "../features/build/shared/queries";
import type { AgentStatus, AgentSummary } from "../features/build/shared/types";
import { usePermissions } from "../session/permissions";
import { useWorkspaceId } from "../session/workspace-context";

/** Agents for the agent switcher and scope pickers (`GET /v1/agents`). */
export interface AgentOption {
  id: string;
  name: string;
  /** "Public · Widget, Messenger" — lowest audience label + deployed channels. */
  meta?: string;
  status: AgentStatus;
}

export type AgentsState =
  | { status: "loading"; agents: readonly AgentOption[] }
  | { status: "error"; agents: readonly AgentOption[] }
  /** The member lacks `agents.view`: no request is made (the API would answer 403) and the list stays empty. */
  | { status: "unavailable"; agents: readonly AgentOption[] }
  | { status: "ready"; agents: readonly AgentOption[] };

const LAST_AGENT_KEY = "sg-last-agent";

function readLastAgent(wsId: string): string | null {
  try {
    return window.sessionStorage.getItem(`${LAST_AGENT_KEY}:${wsId}`);
  } catch {
    return null;
  }
}

function writeLastAgent(wsId: string, agentId: string): void {
  try {
    window.sessionStorage.setItem(`${LAST_AGENT_KEY}:${wsId}`, agentId);
  } catch {
    // Storage blocked (private mode / in-app browser): the URL still carries the agent.
  }
}

function useMeta(canSeeAudiences: boolean): (agent: AgentSummary) => string {
  const { t } = useTranslation();
  const audienceLabel = useAudienceLabels(canSeeAudiences);
  return useCallback(
    (agent: AgentSummary) => {
      const lowest = [...agent.audiences].sort((a, b) => a - b)[0];
      const audience = lowest === undefined ? "" : audienceLabel(lowest);
      const channels = agent.channels.map((c) => t(`build.channels.${c}`, { defaultValue: c })).join(", ");
      return [audience, channels || t("build.agents.notDeployed")].filter(Boolean).join(" · ");
    },
    [audienceLabel, t],
  );
}

export function useAgents(): AgentsState {
  const permissions = usePermissions();
  const permissionsPending = permissions.isPending;
  const canListAgents = permissions.can("agents.view");
  const query = useAgentList(canListAgents);
  const meta = useMeta(permissions.can("audiences.view"));
  return useMemo(() => {
    const agents = (query.data?.items ?? []).map((a) => ({ id: a.id, name: a.name, meta: meta(a), status: a.status }));
    if (permissionsPending) return { status: "loading", agents };
    if (!canListAgents) return { status: "unavailable", agents };
    if (query.isPending) return { status: "loading", agents };
    if (query.isError) return { status: "error", agents };
    return { status: "ready", agents };
  }, [query.data, query.isPending, query.isError, meta, permissionsPending, canListAgents]);
}

/**
 * Active agent: the route's `:agentId` (source of truth, kept in the URL); on workspace pages the
 * agent last opened in this tab, else the first agent.
 */
export function useActiveAgent(routeAgentId: string | null): { state: AgentsState; active: AgentOption | undefined } {
  const state = useAgents();
  const wsId = useWorkspaceId();
  useEffect(() => {
    if (routeAgentId) writeLastAgent(wsId, routeAgentId);
  }, [routeAgentId, wsId]);
  const remembered = routeAgentId ?? readLastAgent(wsId);
  const active = state.agents.find((a) => a.id === remembered) ?? state.agents[0];
  return { state, active };
}

/** Same page for another agent when the path is agent-scoped; else that agent's playground. */
export function agentHref(pathname: string, ws: string, routeAgentId: string | null, nextAgentId: string, fallback: string): string {
  const prefix = `/w/${encodeURIComponent(ws)}/agents/`;
  if (!routeAgentId || !pathname.startsWith(`${prefix}${encodeURIComponent(routeAgentId)}`)) return fallback;
  return `${prefix}${encodeURIComponent(nextAgentId)}${pathname.slice(prefix.length + encodeURIComponent(routeAgentId).length)}`;
}
